<template>
  <section>
    <h2 class="heading-2 mb-8 hidden md:block">
      {{ i18n(translations.filtersTitle) }}
    </h2>
    <fieldset class="block">
      <legend class="body-1-bold">
        {{ i18n(translations.reviewScore) }}
      </legend>
      <div class="mt-8">
        <div aria-hidden="true" class="mb-4 flex items-center justify-between">
          <p class="caption-bold w-fit text-center">
            {{ i18n(translations.one) }}
          </p>

          <p class="caption-bold w-fit text-center">
            {{ i18n(translations.three) }}
          </p>

          <p class="caption-bold w-fit text-center">
            {{ i18n(translations.five) }}
          </p>
        </div>
        <RevRange
          v-model="range"
          class="mx-2"
          :keyboardStep="1"
          :label="[
            i18n(translations.rangeMinAriaLabel),
            i18n(translations.rangeMaxAriaLabel),
          ]"
          :max="5"
          :min="1"
          :showSteps="true"
          :step="1"
          @change="onFilterByRangeClick"
        />
      </div>
    </fieldset>

    <fieldset class="mt-32 block">
      <legend class="body-1-bold">
        {{ i18n(translations.deviceCondition) }}
      </legend>

      <ul class="mt-8 space-y-4">
        <li v-for="grade in gradeList" :key="grade.value">
          <RevCheckbox
            :id="grade.value"
            :model-value="selectedFilterByGrade?.includes(grade.value)"
            :name="grade.value"
            :value="grade.value"
            :variant
            @update:model-value="onSelectedFilterByGradeClick(grade.value)"
          >
            <template #label>{{ grade.label }}</template>
          </RevCheckbox>
        </li>
      </ul>
    </fieldset>

    <fieldset class="mt-32 block">
      <legend class="body-1-bold">
        {{ i18n(translations.togglesSelectorsTitle) }}
      </legend>

      <div v-if="merchantId && showSeller" class="mt-8 flex items-center gap-4">
        <RevToggle
          id="filterBySeller"
          v-model="filterByCurrentSeller"
          :label="i18n(translations.filterReviewsCurrentSeller)"
          @update:model-value="onfilterByCurrentSellerClick"
        />

        <RevTooltip class="caption z-10" position="top">
          <template #trigger="{ show, hide }">
            <IconInfo
              class="size-16"
              @focusin="show"
              @focusout="hide"
              @mouseenter="show"
              @mouseleave="hide"
            />
          </template>

          <p class="mb-4">
            {{ i18n(translations.filterReviewsCurrentSellerTooltipTitle) }}
          </p>

          <p>{{ i18n(translations.filterReviewsCurrentSellerTooltipText) }}</p>
        </RevTooltip>
      </div>

      <RevToggle
        id="filterByPictures"
        v-model="filterByPictures"
        class="mt-8"
        :label="i18n(translations.filterReviewsWithPictures)"
        @update:model-value="onfilterByPicturesClick"
      />

      <RevToggle
        v-if="countryCode !== 'US'"
        id="filterByCountry"
        v-model="filterByCountry"
        class="mt-8"
        :label="
          i18n(translations.filterReviewsCurrentCountry, {
            country: i18n.country(countryCode),
          })
        "
        @update:model-value="onfilterByCountryClick"
      />
    </fieldset>

    <RevButton
      class="mt-32 hidden md:flex"
      fullWidth="always"
      :icon="IconTrash"
      size="small"
      variant="secondary"
      @click="resetFiltersValues"
    >
      {{ i18n(translations.resetFilters) }}
    </RevButton>
  </section>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, inject, watch } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { noop } from '@backmarket/utils/function/noop'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox, type Variants } from '@ds/components/Checkbox'
import { RevRange } from '@ds/components/Range'
import { RevToggle } from '@ds/components/Toggle'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconTrash } from '@ds/icons/IconTrash'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'

import { TRACKING_ZONE, scrollToTargetKey } from '../../constants'

import translations from './Filters.translations'
import { useFilters } from './composables/useFilters'
import { useGradeFilter } from './composables/useGradeFilter'
import { useRatingFilters } from './composables/useRatingFilters'
import { useToggleFilters } from './composables/useToggleFilters'

const { market } = useMarketplace()

const { merchantId, resetFilters } = defineProps<{
  merchantId?: string
  resetFilters?: number
  variant?: Variants
}>()

const route = useRoute()
const i18n = useI18n()
const experiments = useExperiments()
const locale = useI18nLocale()

const { trackClick } = useTracking()

const scrollToTarget = inject(scrollToTargetKey, noop())

const { computeGradeQuery, gradeList, selectedFilterByGrade } = useGradeFilter()
const { resetFiltersValues } = useFilters(scrollToTarget)
const { filterByCurrentSeller, filterByPictures, filterByCountry } =
  useToggleFilters()
const { range, navigateAndTrackRange } = useRatingFilters()

const countryCode = computed(() => market.countryCode)

const showSeller =
  experiments['experiment.reviewSellerAndFilters'] === 'showSeller'

// Helper function to navigate with scroll
async function navigateWithScroll(
  navigationParams: Parameters<typeof navigateTo>[0],
) {
  scrollToTarget?.()
  await navigateTo(navigationParams)
}

async function onFilterByRangeClick(newRange: [number, number]) {
  await navigateAndTrackRange(newRange)

  scrollToTarget?.()
}

async function onSelectedFilterByGradeClick(selectedGrade: string) {
  if (!selectedGrade) return

  const routeName = route.name?.toString() || ''

  const { backbox_grade_label__in: gradeQuery } = route.query
  const newGradeQuery = computeGradeQuery(gradeQuery, selectedGrade)

  if (newGradeQuery) {
    trackClick({
      pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
      zone: TRACKING_ZONE,
      name: `review_grade_filter_${
        Array.isArray(newGradeQuery) ? newGradeQuery.join('_') : newGradeQuery
      }`.toLowerCase(),
    })
  }

  await navigateWithScroll({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      backbox_grade_label__in: newGradeQuery,
      cursor: undefined,
    },
  })
}

async function onfilterByCurrentSellerClick() {
  if (!merchantId) return

  const routeName = route.name?.toString() || ''

  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
    zone: TRACKING_ZONE,
    name: 'review_seller',
  })

  await navigateWithScroll({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      ...(filterByCurrentSeller.value
        ? { seller_public_id: merchantId }
        : { seller_public_id: undefined }),
      cursor: undefined,
    },
  })
}

async function onfilterByPicturesClick() {
  const routeName = route.name?.toString() || ''

  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
    zone: TRACKING_ZONE,
    name: 'review_pictures_filter',
  })

  await navigateWithScroll({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      ...(filterByPictures.value
        ? { with_attachments: 'true' }
        : { with_attachments: undefined }),
      cursor: undefined,
    },
  })
}

async function onfilterByCountryClick() {
  const routeName = route.name?.toString() || ''

  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
    zone: TRACKING_ZONE,
    name: 'review_country_filter',
  })

  await navigateWithScroll({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      ...(filterByCountry.value ? { locale } : { locale: undefined }),
      cursor: undefined,
    },
  })
}

watch(
  () => resetFilters,
  async () => await resetFiltersValues(),
)
</script>
